<template>
  <div class="page-select">
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {{ entriesPerPageLiveMessage }}
    </span>
    <div
      class="page-select-showing d-inline-block"
      v-if="showingEntries && pagination && pagination.total > 0"
    >
      <span
        >{{ $t("common.showing") }}
        {{
          pagination.per_page.label == $t("common.all_entries")
            ? 1
            : pagination.from
        }}
        {{ $t("common.showing_to") }}
        {{
          pagination.to < pagination.total ? pagination.to : pagination.total
        }}
        {{ $t("common.showing_of") }} {{ pagination.total }}
        {{ $t("common.entries") }}
      </span>
    </div>
    <div v-if="pagination">
      <CustomSelect
        :filterable="true"
        :close-on-select="true"
        :options="options || defaultOptions"
        type="textarea"
        v-model="state.selectedOption"
        :searchable="false"
        :input-id="perPageSelectInputId"
        :accessible-label="$t('common.entries_per_page')"
        append-dropdown-to-body
        @update:model-value="onPageChange"
      ></CustomSelect>
    </div>
  </div>
</template>

<script>
import { computed, reactive, watchEffect, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { announce } from "@/composables/useLiveAnnouncer"
import CustomSelect from "../Form/CustomSelect.vue"

const makePerPageInputId = () =>
  `per-page-select-${Math.random().toString(36).slice(2, 11)}`

export default {
  components: { CustomSelect },
  name: "Perpage",
  emits: ["onPageChange"],
  props: {
    /** Stable id for the rows-per-page combobox (a11y + tests); falls back to random when empty. */
    selectInputId: {
      type: String,
      default: ""
    },
    useDynamicUpdate: {
      type: Boolean,
      default: false
    },
    useFirstOptionAsDefault: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object
    },
    options: {
      type: Array,
      deep: true,
      default: null
    },
    action: {
      type: Function
    },
    showingEntries: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const trimmedStableId = props.selectInputId?.trim?.() || ""
    const perPageSelectInputId = trimmedStableId || makePerPageInputId()

    const defaultOptions = computed(() => [
      {
        label: "25",
        value: 25
      },
      {
        label: "50",
        value: 50
      },
      {
        label: "100",
        value: 100
      },
      {
        label: t("common.all_entries"),
        value: 75
      }
    ])

    const defaultValue = { label: "25", value: 25 }
    const state = reactive({
      selectedOption: props.useFirstOptionAsDefault
        ? props?.options?.[0] || defaultValue
        : defaultValue
    })

    const pagination = computed(() => {
      return props?.pagination || {}
    })

    const entriesPerPageLiveMessage = computed(() => {
      // Prefer numeric value for accessibility, fall back safely
      const rawValue =
        state?.selectedOption?.value ?? props?.pagination?.per_page?.value ?? 25

      let numericValue = 25

      if (typeof rawValue === "number") {
        numericValue = rawValue
      } else if (rawValue !== null && rawValue !== undefined) {
        const rawString = String(rawValue)
        const parsed = parseInt(rawString.split("/")[0], 10)
        if (!Number.isNaN(parsed)) {
          numericValue = parsed
        }
      }

      return `${t("common.entries_per_page")}: ${numericValue}`
    })

    const onPageChange = (value) => {
      emit("onPageChange", value)
      state.selectedOption = value
      nextTick(() => {
        announce(entriesPerPageLiveMessage.value)
      })
    }

    watchEffect(() => {
      if (!props.useDynamicUpdate) return

      const perPageValue = pagination.value?.per_page?.value
      if (!Array.isArray(props?.options) || perPageValue === undefined) return

      const matchedOption = props.options.find(
        (option) => option?.value === perPageValue
      )
      if (matchedOption) {
        state.selectedOption = matchedOption
      }
    })

    return {
      state,
      onPageChange,
      defaultOptions,
      perPageSelectInputId,
      entriesPerPageLiveMessage
    }
  }
}
</script>
